import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import { useRosterTemplateLibrary } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import {
  useArchiveFilledRosterTemplate,
  useFilledRosterTemplates,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';

import {
  FilledRosterTemplateEditorDialog,
  type FilledTemplateEditorSeed,
} from './FilledRosterTemplateEditorDialog';
import { StartFromDialog } from './StartFromDialog';

type EditorState = {
  existing: FilledRosterTemplate | null;
  seed: FilledTemplateEditorSeed;
};

/**
 * Shown for HWCG orgs, and for any org that already has filled templates so
 * they can still be removed.
 */
export function FilledRosterTemplatesSection({ hwcg }: { hwcg: boolean }) {
  const templates = useFilledRosterTemplates();
  const library = useRosterTemplateLibrary();
  const catalog = useRosterTemplatesCatalog();
  const archive = useArchiveFilledRosterTemplate();
  const [starterOpen, setStarterOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [toDelete, setToDelete] = useState<FilledRosterTemplate | null>(null);

  const libraryTemplates = useMemo(
    () => (library.data ?? []).filter((entry) => entry.nodes.length > 0),
    [library.data]
  );

  const openExisting = (template: FilledRosterTemplate) => {
    const nodes = nodesFor(
      template,
      library.data ?? [],
      catalog.data?.templates ?? []
    );
    setEditor({
      existing: template,
      seed: {
        name: template.name,
        description: null,
        attachOnly: false,
        nodes,
        sourceTemplateId: template.sourceTemplateId,
        sourceSlug: template.sourceSlug,
      },
    });
  };

  if (!hwcg && (templates.data ?? []).length === 0) return null;

  return (
    <section className="tw:flex tw:min-h-0 tw:flex-col tw:gap-3">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div>
          <div className="tw:text-sm tw:font-medium">
            Filled Roster Templates
          </div>
          <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
            A saved structure with people from this organization. Create
            incident can apply one so those people start on the seats.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setStarterOpen(true)}>
          <Plus className="tw:size-4" />
          New Filled Template
        </Button>
      </div>
      <div className="tw:rounded-md tw:border tw:border-border">
        {(templates.data ?? []).length === 0 ? (
          <p className="tw:m-0 tw:p-3 tw:text-xs tw:text-muted-foreground">
            No filled templates yet.
          </p>
        ) : (
          <ul className="tw:m-0 tw:divide-y tw:divide-border tw:p-0">
            {(templates.data ?? []).map((template) => (
              <li
                key={template.id}
                className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-3 tw:py-2"
              >
                <button
                  type="button"
                  className="tw:text-left tw:text-sm"
                  onClick={() => openExisting(template)}
                >
                  {template.name}
                  <span className="tw:ml-2 tw:text-xs tw:text-muted-foreground">
                    {template.assignments.length} assigned
                  </span>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Delete ${template.name}`}
                  title="Delete"
                  disabled={archive.isPending}
                  onClick={() => setToDelete(template)}
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <StartFromDialog
        open={starterOpen}
        onClose={() => setStarterOpen(false)}
        title="New filled roster template"
        description="Pick a roster structure to seed the chart, then assign one or more people to each seat."
        allowBlank={false}
        libraryTemplates={libraryTemplates}
        onChoose={({ seed, sourceTemplateId, sourceSlug }) => {
          setStarterOpen(false);
          setEditor({
            existing: null,
            seed: {
              ...seed,
              // Drop the "Copy of" prefix for filled templates — the structure
              // is a seed, not a duplicate of a named structure template.
              name: seed.name.replace(/^Copy of\s+/i, ''),
              sourceTemplateId,
              sourceSlug,
            },
          });
        }}
      />

      <ConfirmDeleteDialog
        open={toDelete !== null}
        title="Delete filled roster"
        description={
          toDelete
            ? `Delete ${toDelete.name}? Create incident will no longer offer it. Workspaces already started from it keep their people.`
            : ''
        }
        pending={archive.isPending}
        onOpenChange={(next) => {
          if (!next) setToDelete(null);
        }}
        onConfirm={() => {
          if (!toDelete) return;
          archive.mutate(toDelete.id, { onSuccess: () => setToDelete(null) });
        }}
      />

      {editor ? (
        <FilledRosterTemplateEditorDialog
          existing={editor.existing}
          seed={editor.seed}
          onClose={() => setEditor(null)}
        />
      ) : null}
    </section>
  );
}

function nodesFor(
  template: FilledRosterTemplate,
  libraryEntries: { id: string; nodes: RosterTemplateNodeDto[] }[],
  catalog: { slug: string; nodes?: RosterTemplateNodeDto[] }[]
): RosterTemplateNodeDto[] {
  if (template.sourceTemplateId) {
    return (
      libraryEntries.find((entry) => entry.id === template.sourceTemplateId)
        ?.nodes ?? []
    );
  }
  if (template.sourceSlug) {
    return (
      catalog.find((entry) => entry.slug === template.sourceSlug)?.nodes ?? []
    );
  }
  return [];
}
